import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Pencil, Plus, RefreshCw, Send } from 'lucide-react';
import {
  Badge,
  Button,
  buttonStyles,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { useMe, usePermissions } from '../lib/session';
import type {
  AdAccount,
  AdAccounts,
  AdCampaignRow,
  AdChangeRequest,
  AdImportResult,
  AdsHistory,
  AdsReport,
  AdSyncResult,
  GoogleAdsSettings,
} from '../lib/types';
import { FilePicker } from './imports';
import { AdAiSections } from './ai/drafts';

/**
 * Pazarlama → Reklam merkezi (F6-17, ADR-098): reklam hesapları ve veri kaynakları (Meta/TikTok API eşitlemesi
 * veya platform raporu dosyası), kampanya performansı (gerçek sipariş geliriyle ROAS, CPA ve maliyet varsa kâr),
 * kampanya karşılaştırma, ürün performansı, anomali uyarıları, bütçe/yayın değişikliği istek–onay akışı ve geçmiş.
 * Bu ekran reklam platformunda hiçbir değişikliği kendiliğinden yapmaz.
 */

const PLATFORMS = ['google_ads', 'meta', 'tiktok', 'other'] as const;
type Platform = (typeof PLATFORMS)[number];
const DAYS = ['7', '30', '90'] as const;
const CONNECTIONS = ['meta_ads', 'tiktok_ads', 'google_ads'] as const;
const MAX_COMPARE = 4;

const changeTone: Record<AdChangeRequest['status'], Tone> = {
  pending: 'amber',
  approved: 'pine',
  rejected: 'rose',
  applied: 'moss',
  cancelled: 'neutral',
};
const runTone: Record<string, Tone> = { ok: 'moss', partial: 'amber', failed: 'rose' };

function useFormat() {
  const { locale } = useI18n();
  const tag = locale === 'en' ? 'en-GB' : 'tr-TR';
  const int = new Intl.NumberFormat(tag);
  const pct = new Intl.NumberFormat(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const roas = new Intl.NumberFormat(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const date = new Intl.DateTimeFormat(tag, { dateStyle: 'medium' });
  const dateTime = new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' });
  return {
    int: (n: number) => int.format(n),
    /** Baz puan → "%1,25" (yalnızca gösterim). */
    pct: (bps: number | null) => (bps === null ? '—' : `%${pct.format(bps / 100)}`),
    /** ROAS baz puanı → "3,25×". */
    roas: (bps: number | null) => (bps === null ? '—' : `${roas.format(bps / 10_000)}×`),
    day: (d: string) => date.format(new Date(`${d}T12:00:00Z`)),
    at: (d: string | null) => (d ? dateTime.format(new Date(d)) : '—'),
  };
}

export function AdsPage() {
  const t = useT();
  return (
    <PageFrame
      helpKey="page.ads"
      actions={
        <div className="flex items-center gap-1">
          <Link to="/reklam-merkezi/google-ads" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.nav.googleAdsCampaigns')}
          </Link>
          <HelpTip k="page.google_ads_campaigns" />
        </div>
      }
    >
      <SourcesSection />
      <ReportSection />
      <AdAiSections />
      <HistorySection />
      <GoogleConversionsSection />
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Hesaplar ve veri

function SourcesSection() {
  const t = useT();
  const f = useFormat();
  const { can } = usePermissions();
  const manage = can('marketing.ads.manage');
  const q = useQuery({
    queryKey: ['ad-accounts'],
    queryFn: () => api.get<AdAccounts>('/v1/admin/ads/accounts'),
  });
  const [editing, setEditing] = useState<AdAccount | 'new' | null>(null);
  const [importing, setImporting] = useState<AdAccount | null>(null);
  // Google Ads ancak bir mağazaya müşteri hesabı bağlanınca eşitlenir (Google bağlantıları, F6-18).
  const live = CONNECTIONS.filter(
    (c) => q.data?.connections[c] === 'LIVE' && (c !== 'google_ads' || q.data.googleAdsLinked),
  );
  const sync = useApiMutation(() => api.post<AdSyncResult>('/v1/admin/ads/sync'), {
    invalidate: [['ad-accounts'], ['ads-report'], ['ads-history']],
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length)
        toast.error(t('admin.ads.syncFailed', { error: t(`integrationMessages.${failed[0]!.error}`) }));
      else toast.success(t('admin.ads.synced', { rows: r.results.reduce((a, x) => a + x.rows, 0) }));
    },
  });
  return (
    <Section
      title={t('admin.ads.sourcesTitle')}
      description={t('admin.ads.sourcesLead')}
      action={
        manage ? (
          <div className="flex flex-wrap items-center gap-2">
            {live.length > 0 && (
              <>
                <Button loading={sync.isPending} onClick={() => sync.mutate(undefined)}>
                  <RefreshCw className="h-4 w-4" aria-hidden />
                  {t('admin.ads.syncNow')}
                </Button>
                <HelpTip k="ads.sync_now" />
              </>
            )}
            <Button variant="primary" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.ads.addAccount')}
            </Button>
            <HelpTip k="ads.add_account" />
          </div>
        ) : undefined
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <ul className="mb-4 flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label={t('admin.ads.connections')}>
            {CONNECTIONS.map((c) => {
              const status = q.data.connections[c];
              return (
                <li key={c} className="flex items-center gap-2">
                  <span>{t(`integrations.${c}.name`)}</span>
                  {status === 'LIVE' ? (
                    <Badge tone="moss">{t('admin.ads.connected')}</Badge>
                  ) : status === 'ERROR' ? (
                    <Badge tone="rose">{t('admin.ads.connectionError')}</Badge>
                  ) : (
                    <Badge tone="amber">{t('admin.ads.setupRequired')}</Badge>
                  )}
                </li>
              );
            })}
            <li className="flex items-center gap-1">
              <Link
                to="/entegrasyonlar"
                className="text-pine underline underline-offset-2 hover:no-underline"
              >
                {t('admin.ads.manageConnections')}
              </Link>
              <HelpTip k="ads.connections" />
            </li>
          </ul>
          {(q.data.connections.google_ads !== 'LIVE' || !q.data.googleAdsLinked) && (
            <p className="mb-4 text-sm text-ink-2">
              {t('admin.ads.googleNote')}{' '}
              <Link to="/google" className="text-pine underline underline-offset-2 hover:no-underline">
                {t('admin.ads.googleNoteLink')}
              </Link>
            </p>
          )}
          {q.data.accounts.length === 0 ? (
            <EmptyState title={t('admin.ads.noAccounts')} description={t('admin.ads.noAccountsLead')} />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.ads.accountsTable')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.account')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.source')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.utmSources')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.lastData')}</th>
                    {manage && (
                      <th className="px-3 py-2 text-end font-medium">
                        <span className="sr-only">{t('common.actions')}</span>
                        <HelpTip k="ads.account_actions" />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {q.data.accounts.map((a) => (
                    <tr key={a.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{a.name}</div>
                        <div className="text-ink-3">
                          {t(`admin.ads.platforms.${a.platform}`)} · {a.currency}
                          {a.status === 'archived' && (
                            <Badge tone="neutral" className="ms-2">
                              {t('admin.ads.archived')}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={a.source === 'api' ? 'pine' : 'slate'}>
                          {t(`admin.ads.sources.${a.source}`)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-ink-2">
                        {a.utmSources.length ? (
                          a.utmSources.join(', ')
                        ) : (
                          <Badge tone="amber">{t('admin.ads.noUtm')}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {a.source === 'api' ? f.at(a.lastSyncAt) : '—'}
                        {a.lastSyncError && (
                          <p className="text-rose">{t(`integrationMessages.${a.lastSyncError}`)}</p>
                        )}
                      </td>
                      {manage && (
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap justify-end gap-2">
                            {a.source === 'file' && a.status === 'active' && (
                              <Button size="sm" onClick={() => setImporting(a)}>
                                {t('admin.ads.uploadReport')}
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                              <Pencil className="h-4 w-4" aria-hidden />
                              <span className="sr-only">
                                {t('admin.ads.editAccountFor', { name: a.name })}
                              </span>
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {editing && (
        <AccountDialog account={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {importing && <ImportDialog account={importing} onClose={() => setImporting(null)} />}
    </Section>
  );
}

function AccountDialog({ account, onClose }: { account: AdAccount | null; onClose: () => void }) {
  const t = useT();
  const [platform, setPlatform] = useState<Platform>((account?.platform as Platform) ?? 'meta');
  const [name, setName] = useState(account?.name ?? '');
  const [externalId, setExternalId] = useState('');
  const [currency, setCurrency] = useState(account?.currency ?? 'TRY');
  const [utm, setUtm] = useState(account ? account.utmSources.join(', ') : '');
  const [status, setStatus] = useState<'active' | 'archived'>(account?.status ?? 'active');
  const utmSources = utm
    .split(/[,\s]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
  const save = useApiMutation(
    () =>
      account
        ? api.patch(`/v1/admin/ads/accounts/${account.id}`, {
            name,
            utmSources,
            status,
            version: account.version,
          })
        : api.post('/v1/admin/ads/accounts', {
            platform,
            name,
            externalId: externalId || undefined,
            currency,
            utmSources: utmSources.length ? utmSources : undefined,
          }),
    {
      invalidate: [['ad-accounts'], ['ads-report']],
      success: t('admin.ads.accountSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const err = (k: string) => fieldError(save.error, k);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={account ? t('admin.ads.editAccount') : t('admin.ads.addAccount')}
      description={account ? undefined : t('admin.ads.addAccountLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
          <HelpTip k="ads.save_account" />
        </>
      }
    >
      <div className="space-y-4">
        {!account && (
          <Field label={t('admin.ads.platform')} required help={<HelpTip k="ads.account_platform" />}>
            {(p) => (
              <Select {...p} value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
                {PLATFORMS.map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.ads.platforms.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field
          label={t('admin.ads.accountName')}
          required
          help={<HelpTip k="ads.account_name" />}
          error={err('name')}
        >
          {(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
        </Field>
        {!account && (
          <>
            <Field
              label={t('admin.ads.externalId')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="ads.account_external_id" />}
              error={err('externalId')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={externalId}
                  maxLength={40}
                  onChange={(e) => setExternalId(e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('admin.ads.currency')}
              required
              help={<HelpTip k="ads.account_currency" />}
              error={err('currency')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={currency}
                  maxLength={3}
                  className="w-24 uppercase"
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              )}
            </Field>
          </>
        )}
        <Field
          label={t('admin.ads.utmSources')}
          optionalLabel={account ? undefined : t('common.optional')}
          description={account ? undefined : t('admin.ads.utmDefault')}
          help={<HelpTip k="ads.account_utm_sources" />}
          error={err('utmSources') ?? err('utmSources.0')}
        >
          {(p) => (
            <Input
              {...p}
              value={utm}
              placeholder="facebook, instagram"
              onChange={(e) => setUtm(e.target.value)}
            />
          )}
        </Field>
        {account && (
          <Field label={t('admin.ads.accountStatus')} help={<HelpTip k="ads.account_status" />}>
            {(p) => (
              <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                <option value="active">{t('admin.ads.active')}</option>
                <option value="archived">{t('admin.ads.archived')}</option>
              </Select>
            )}
          </Field>
        )}
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function ImportDialog({ account, onClose }: { account: AdAccount; onClose: () => void }) {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AdImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (picked: File, dryRun: boolean) => {
    const fd = new FormData();
    fd.append('file', picked);
    return api.upload<AdImportResult>(`/v1/admin/ads/accounts/${account.id}/import?dryRun=${dryRun}`, fd);
  };
  const choose = async (picked: File) => {
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      setPreview(await send(picked, true));
      setFile(picked);
    } catch (e) {
      setError(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  const apply = useApiMutation(() => send(file!, false), {
    invalidate: [['ads-report'], ['ads-history'], ['ad-accounts']],
    success: (r) => t('admin.ads.imported', { inserted: r.inserted, updated: r.updated }),
    onSuccess: onClose,
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.ads.uploadTitle', { name: account.name })}
      description={t('admin.ads.uploadLead', { currency: account.currency })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!preview || preview.valid === 0}
            loading={apply.isPending}
            onClick={() => apply.mutate(undefined)}
          >
            {t('admin.ads.applyImport')}
          </Button>
          <HelpTip k="ads.apply_import" />
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-1">
          <FilePicker busy={busy} onFile={(x) => void choose(x)} label={t('admin.ads.chooseFile')} />
          <HelpTip k="ads.upload_file" />
        </div>
        {error && (
          <p role="alert" className="text-sm text-rose">
            {error}
          </p>
        )}
        {preview && (
          <div aria-live="polite" className="space-y-3">
            <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[minmax(9rem,auto)_1fr]">
              <dt className="text-ink-3">{t('admin.ads.previewFile')}</dt>
              <dd>{file?.name}</dd>
              <dt className="text-ink-3">{t('admin.ads.previewRows')}</dt>
              <dd>
                {t('admin.ads.previewRowsValue', {
                  valid: preview.valid,
                  rows: preview.rows,
                  skipped: preview.skipped,
                })}
              </dd>
              <dt className="text-ink-3">{t('admin.ads.previewPeriod')}</dt>
              <dd>{preview.period ? `${f.day(preview.period.from)} – ${f.day(preview.period.to)}` : '—'}</dd>
              <dt className="text-ink-3">{t('admin.ads.previewSpend')}</dt>
              <dd>{fmt(preview.spendMinor, preview.currency)}</dd>
              <dt className="text-ink-3">{t('admin.ads.previewCampaigns')}</dt>
              <dd>{preview.campaigns}</dd>
            </dl>
            {preview.errors.length > 0 && (
              <div>
                <p className="text-sm font-medium">{t('admin.ads.previewErrors')}</p>
                <ul className="mt-1 list-disc ps-5 text-sm text-ink-2">
                  {preview.errors.map((e) => (
                    <li key={`${e.row}-${e.code}`}>
                      {t('admin.ads.rowError', { row: e.row, error: t(`admin.ads.rowErrors.${e.code}`) })}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Rapor

function ReportSection() {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  const { can } = usePermissions();
  const manage = can('marketing.ads.manage');
  const [days, setDays] = useState<(typeof DAYS)[number]>('30');
  const [platform, setPlatform] = useState<Platform | ''>('');
  const [compare, setCompare] = useState<string[]>([]);
  const [changing, setChanging] = useState<AdCampaignRow | null>(null);
  const q = useQuery({
    queryKey: ['ads-report', days, platform],
    queryFn: () =>
      api.get<AdsReport>(`/v1/admin/ads/report?days=${days}${platform ? `&platform=${platform}` : ''}`),
  });
  const r = q.data;
  const keyOf = (c: AdCampaignRow) => `${c.accountId}|${c.campaignKey}`;
  const compared = useMemo(
    () =>
      r
        ? compare.map((k) => r.campaigns.find((c) => keyOf(c) === k)).filter((c): c is AdCampaignRow => !!c)
        : [],
    [r, compare],
  );
  const toggle = (k: string) =>
    setCompare((xs) =>
      xs.includes(k) ? xs.filter((x) => x !== k) : xs.length >= MAX_COMPARE ? xs : [...xs, k],
    );
  const campaignLabel = (c: AdCampaignRow) =>
    c.unmatched ? t('admin.ads.unmatchedCampaign') : c.campaignName;
  return (
    <Section
      title={t('admin.ads.reportTitle')}
      description={t('admin.ads.reportLead')}
      action={<HelpTip k="ads.report" />}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Field label={t('admin.ads.period')} help={<HelpTip k="ads.period" />}>
          {(p) => (
            <Select {...p} value={days} onChange={(e) => setDays(e.target.value as typeof days)}>
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {t('admin.ads.lastDays', { n: Number(d) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.ads.platform')} help={<HelpTip k="ads.platform_filter" />}>
          {(p) => (
            <Select {...p} value={platform} onChange={(e) => setPlatform(e.target.value as Platform | '')}>
              <option value="">{t('admin.ads.allPlatforms')}</option>
              {PLATFORMS.map((x) => (
                <option key={x} value={x}>
                  {t(`admin.ads.platforms.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!r ? (
        <Spinner label={t('common.loading')} />
      ) : r.campaigns.length === 0 ? (
        <EmptyState title={t('admin.ads.noData')} description={t('admin.ads.noDataLead')} />
      ) : (
        <div className="space-y-6">
          {r.totals.map((x) => (
            <dl
              key={x.currency}
              className="grid gap-3 sm:grid-cols-4"
              aria-label={t('admin.ads.totalsLabel', { currency: x.currency })}
            >
              {[
                { label: t('admin.ads.spend'), value: fmt(x.spendMinor, x.currency) },
                { label: t('admin.ads.revenue'), value: fmt(x.revenueMinor, x.currency) },
                { label: t('admin.ads.roas'), value: f.roas(x.roasBps) },
                { label: t('admin.ads.orders'), value: f.int(x.orders) },
                { label: t('admin.ads.newCustomers'), value: f.int(x.newCustomers) },
                { label: t('admin.ads.cpa'), value: x.cpaMinor === null ? '—' : fmt(x.cpaMinor, x.currency) },
                { label: t('admin.ads.cac'), value: x.cacMinor === null ? '—' : fmt(x.cacMinor, x.currency) },
                { label: t('admin.ads.ctr'), value: f.pct(x.ctrBps) },
              ].map((s) => (
                <div key={s.label} className="rounded-md border border-line bg-surface px-3 py-2">
                  <dt className="text-xs text-ink-3">{s.label}</dt>
                  <dd className="mt-0.5 text-[0.9375rem] font-medium tabular">{s.value}</dd>
                </div>
              ))}
            </dl>
          ))}
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.ads.campaignsTable')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">
                    <span className="sr-only">{t('admin.ads.compare')}</span>
                  </th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.ads.campaign')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.spend')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.impressions')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.clicks')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.ctr')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.cpc')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.cpm')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.platformConversions')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.orders')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.revenue')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.cpa')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.cac')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ads.roas')}</th>
                  {r.costVisible && (
                    <th className="px-3 py-2 text-end font-medium">{t('admin.ads.profit')}</th>
                  )}
                  {manage && (
                    <th className="px-3 py-2 text-end font-medium">
                      <span className="sr-only">{t('common.actions')}</span>
                      <HelpTip k="ads.request_change" />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {r.campaigns.map((c) => {
                  const k = keyOf(c);
                  const money = (v: number | null) => (v === null ? '—' : fmt(v, c.currency));
                  return (
                    <tr key={k} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        {!c.unmatched && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-pine"
                            checked={compare.includes(k)}
                            disabled={!compare.includes(k) && compare.length >= MAX_COMPARE}
                            aria-label={t('admin.ads.compareThis', { name: c.campaignName })}
                            onChange={() => toggle(k)}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className={c.unmatched ? 'italic text-ink-2' : 'font-medium'}>
                          {campaignLabel(c)}
                        </div>
                        <div className="text-ink-3">
                          {t(`admin.ads.platforms.${c.platform}`)} · {c.accountName}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">{fmt(c.spendMinor, c.currency)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{f.int(c.impressions)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{f.int(c.clicks)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{f.pct(c.ctrBps)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{money(c.cpcMinor)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{money(c.cpmMinor)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{c.platformConversions || '—'}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{f.int(c.orders)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{fmt(c.revenueMinor, c.currency)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{money(c.cpaMinor)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{money(c.cacMinor)}</td>
                      <td className="px-3 py-2 text-end font-medium tabular-nums">{f.roas(c.roasBps)}</td>
                      {r.costVisible && (
                        <td
                          className={`px-3 py-2 text-end tabular-nums ${c.profitMinor !== null && c.profitMinor < 0 ? 'text-rose' : ''}`}
                        >
                          {money(c.profitMinor)}
                        </td>
                      )}
                      {manage && (
                        <td className="px-3 py-2 text-end">
                          {!c.unmatched && (
                            <Button size="sm" onClick={() => setChanging(c)}>
                              {t('admin.ads.requestChange')}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="space-y-1 text-sm text-ink-3">
            <p>{t('admin.ads.revenueNote')}</p>
            <p>{r.costVisible ? t('admin.ads.profitNote') : t('admin.ads.profitHidden')}</p>
            {r.unmatchedCurrencyOrders > 0 && (
              <p>{t('admin.ads.currencyMismatch', { n: r.unmatchedCurrencyOrders })}</p>
            )}
            <p className="flex items-center gap-1">
              {t('admin.ads.compareHint', { n: MAX_COMPARE })} <HelpTip k="ads.compare" />
            </p>
          </div>
          {compared.length >= 2 && <CompareTable rows={compared} label={campaignLabel} />}
          <AnomalyList report={r} />
          <ProductTable report={r} />
          <ProductAdsTable report={r} />
        </div>
      )}
      {changing && <ChangeDialog campaign={changing} onClose={() => setChanging(null)} />}
    </Section>
  );
}

function CompareTable({ rows, label }: { rows: AdCampaignRow[]; label: (c: AdCampaignRow) => string }) {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  const metrics: Array<{ label: string; value: (c: AdCampaignRow) => string }> = [
    { label: t('admin.ads.spend'), value: (c) => fmt(c.spendMinor, c.currency) },
    { label: t('admin.ads.impressions'), value: (c) => f.int(c.impressions) },
    { label: t('admin.ads.clicks'), value: (c) => f.int(c.clicks) },
    { label: t('admin.ads.ctr'), value: (c) => f.pct(c.ctrBps) },
    { label: t('admin.ads.cpc'), value: (c) => (c.cpcMinor === null ? '—' : fmt(c.cpcMinor, c.currency)) },
    { label: t('admin.ads.orders'), value: (c) => f.int(c.orders) },
    { label: t('admin.ads.revenue'), value: (c) => fmt(c.revenueMinor, c.currency) },
    { label: t('admin.ads.newCustomers'), value: (c) => f.int(c.newCustomers) },
    { label: t('admin.ads.cpa'), value: (c) => (c.cpaMinor === null ? '—' : fmt(c.cpaMinor, c.currency)) },
    { label: t('admin.ads.cac'), value: (c) => (c.cacMinor === null ? '—' : fmt(c.cacMinor, c.currency)) },
    { label: t('admin.ads.roas'), value: (c) => f.roas(c.roasBps) },
  ];
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={t('admin.ads.compareTitle')}>
        <caption className="px-3 pt-2 text-start font-medium">{t('admin.ads.compareTitle')}</caption>
        <thead className="text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">
              <span className="sr-only">{t('admin.ads.metric')}</span>
            </th>
            {rows.map((c) => (
              <th
                key={`${c.accountId}|${c.campaignKey}`}
                scope="col"
                className="px-3 py-2 text-end font-medium"
              >
                {label(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.label} className="border-t border-line">
              <th scope="row" className="px-3 py-2 text-start font-normal text-ink-2">
                {m.label}
              </th>
              {rows.map((c) => (
                <td key={`${c.accountId}|${c.campaignKey}`} className="px-3 py-2 text-end tabular-nums">
                  {m.value(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnomalyList({ report }: { report: AdsReport }) {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-1 font-medium">
        {t('admin.ads.anomaliesTitle')} <HelpTip k="ads.anomalies" />
      </h3>
      {report.anomalies.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.ads.noAnomalies')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {report.anomalies.map((a, i) => (
            <li
              key={`${a.kind}-${a.accountName}-${a.campaignName}-${i}`}
              className="flex flex-wrap items-center gap-2"
            >
              <Badge tone={a.kind.startsWith('sync') ? 'rose' : 'amber'}>
                {t(`admin.ads.anomalyKinds.${a.kind}`)}
              </Badge>
              <span>
                {t(`admin.ads.anomalyText.${a.kind}`, {
                  account: a.accountName,
                  campaign: a.campaignName,
                  date: f.day(a.date),
                  value: a.valueMinor === undefined ? '' : fmt(a.valueMinor, a.currency),
                  baseline: a.baselineMinor === undefined ? '' : fmt(a.baselineMinor, a.currency),
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductTable({ report }: { report: AdsReport }) {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  if (!report.products.length) return null;
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={t('admin.ads.productsTitle')}>
        <caption className="flex items-center gap-1 px-3 pt-2 text-start font-medium">
          {t('admin.ads.productsTitle')} <HelpTip k="ads.products" />
        </caption>
        <thead className="text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.ads.product')}</th>
            <th className="px-3 py-2 text-start font-medium">{t('admin.ads.platform')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.quantity')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.productRevenue')}</th>
          </tr>
        </thead>
        <tbody>
          {report.products.map((p) => (
            <tr key={`${p.platform}|${p.title}|${p.currency}`} className="border-t border-line">
              <td className="px-3 py-2">{p.title}</td>
              <td className="px-3 py-2 text-ink-2">{t(`admin.ads.platforms.${p.platform}`)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.int(p.quantity)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{fmt(p.netMinor, p.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Alışveriş reklamlarında ürün bazlı harcama ve aynı hesaptan gelen siparişlerdeki ürün geliri (F6-21). */
function ProductAdsTable({ report }: { report: AdsReport }) {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  if (!report.productAds.length) return null;
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={t('admin.ads.productAdsTitle')}>
        <caption className="flex items-center gap-1 px-3 pt-2 text-start font-medium">
          {t('admin.ads.productAdsTitle')} <HelpTip k="ads.product_ads" />
        </caption>
        <thead className="text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.ads.product')}</th>
            <th className="px-3 py-2 text-start font-medium">{t('admin.ads.account')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.spend')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.clicks')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.platformConversions')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.quantity')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.productRevenue')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.ads.roas')}</th>
          </tr>
        </thead>
        <tbody>
          {report.productAds.map((p) => (
            <tr key={`${p.accountId}|${p.productKey}`} className="border-t border-line">
              <td className="px-3 py-2">
                {p.productTitle ?? p.productKey}
                <span className="block text-xs text-ink-3">{p.productKey}</span>
              </td>
              <td className="px-3 py-2 text-ink-2">{p.accountName}</td>
              <td className="px-3 py-2 text-end tabular-nums">{fmt(p.spendMinor, p.currency)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.int(p.clicks)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.int(Math.round(p.platformConversions))}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.int(p.quantity)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{fmt(p.revenueMinor, p.currency)}</td>
              <td className="px-3 py-2 text-end font-medium tabular-nums">{f.roas(p.roasBps)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-3 pb-2 text-xs text-ink-3">{t('admin.ads.productAdsNote')}</p>
    </div>
  );
}

function ChangeDialog({ campaign, onClose }: { campaign: AdCampaignRow; onClose: () => void }) {
  const t = useT();
  const [kind, setKind] = useState<'budget' | 'status'>('budget');
  const [budget, setBudget] = useState('');
  const [target, setTarget] = useState<'active' | 'paused'>('paused');
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const budgetMinor = toMinor(budget, campaign.currency);
  const body = {
    accountId: campaign.accountId,
    campaignKey: campaign.campaignKey,
    campaignName: campaign.campaignName,
    kind,
    budgetMinor: kind === 'budget' ? (budgetMinor ?? undefined) : undefined,
    targetStatus: kind === 'status' ? target : undefined,
    note,
  };
  const create = useApiMutation(() => api.post('/v1/admin/ads/changes', body, { idempotencyKey: idem }), {
    invalidate: [['ads-history']],
    success: t('admin.ads.changeRequested'),
    onSuccess: onClose,
    silentFieldErrors: true,
  });
  const budgetInvalid =
    kind === 'budget' && budget.trim() !== '' && (budgetMinor === null || budgetMinor <= 0);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.ads.changeTitle')}
      description={t('admin.ads.changeLead', {
        campaign: campaign.campaignName,
        account: campaign.accountName,
      })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={
              note.trim().length < 3 || (kind === 'budget' && (budgetMinor === null || budgetMinor <= 0))
            }
            onClick={() => create.mutate(undefined)}
          >
            <Send className="h-4 w-4" aria-hidden />
            {t('admin.ads.sendRequest')}
          </Button>
          <HelpTip k="ads.send_request" />
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.ads.changeKind')} help={<HelpTip k="ads.change_kind" />}>
          {(p) => (
            <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="budget">{t('admin.ads.kinds.budget')}</option>
              <option value="status">{t('admin.ads.kinds.status')}</option>
            </Select>
          )}
        </Field>
        {kind === 'budget' ? (
          <Field
            label={t('admin.ads.dailyBudget', { currency: campaign.currency })}
            required
            help={<HelpTip k="ads.change_budget" />}
            error={budgetInvalid ? t('admin.ads.budgetInvalid') : fieldError(create.error, 'budgetMinor')}
          >
            {(p) => (
              <Input {...p} inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} />
            )}
          </Field>
        ) : (
          <Field label={t('admin.ads.targetStatus')} required help={<HelpTip k="ads.change_status" />}>
            {(p) => (
              <Select {...p} value={target} onChange={(e) => setTarget(e.target.value as typeof target)}>
                <option value="paused">{t('admin.ads.targets.paused')}</option>
                <option value="active">{t('admin.ads.targets.active')}</option>
              </Select>
            )}
          </Field>
        )}
        <Field
          label={t('admin.ads.changeNote')}
          required
          help={<HelpTip k="ads.change_note" />}
          error={fieldError(create.error, 'note')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        <p className="text-sm text-ink-3">{t('admin.ads.changeManualNote')}</p>
        {create.error && !create.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {create.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Geçmiş

function HistorySection() {
  const t = useT();
  const fmt = useMoney();
  const f = useFormat();
  const { can, roleKey } = usePermissions();
  const me = useMe();
  const manage = can('marketing.ads.manage');
  const approve = can('marketing.ads.approve');
  const [deciding, setDeciding] = useState<AdChangeRequest | null>(null);
  const q = useQuery({
    queryKey: ['ads-history'],
    queryFn: () => api.get<AdsHistory>('/v1/admin/ads/history'),
  });
  const step = useApiMutation(
    (v: { id: string; action: 'applied' | 'cancel'; version: number }) =>
      api.post(`/v1/admin/ads/changes/${v.id}/${v.action}`, { version: v.version }),
    {
      invalidate: [['ads-history']],
      success: (_r) => t('admin.ads.changeUpdated'),
    },
  );
  const describe = (c: AdChangeRequest) =>
    c.kind === 'budget'
      ? t('admin.ads.budgetTo', { amount: fmt(c.budgetMinor, c.currency) })
      : t(`admin.ads.targets.${c.targetStatus ?? 'paused'}`);
  return (
    <Section
      title={t('admin.ads.historyTitle')}
      description={t('admin.ads.historyLead')}
      action={<HelpTip k="ads.history" />}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-6">
          {q.data.changes.length === 0 ? (
            <p className="text-sm text-ink-2">{t('admin.ads.noChanges')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.ads.changesTable')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.when')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.campaign')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.request')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.ads.status')}</th>
                    <th className="px-3 py-2 text-end font-medium">
                      <span className="sr-only">{t('common.actions')}</span>
                      <HelpTip k="ads.change_actions" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.changes.map((c) => {
                    const own = c.requestedBy === me.data?.user.id;
                    return (
                      <tr key={c.id} className="border-t border-line align-top">
                        <td className="px-3 py-2 whitespace-nowrap">{f.at(c.createdAt)}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{c.campaignName}</div>
                          <div className="text-ink-3">
                            {t(`admin.ads.platforms.${c.platform}`)} · {c.accountName}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div>{describe(c)}</div>
                          <div className="text-ink-2">{c.note}</div>
                          <div className="text-ink-3">
                            {t('admin.ads.requestedBy', { name: c.requesterName ?? '—' })}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={changeTone[c.status]}>
                            {t(`admin.ads.changeStatuses.${c.status}`)}
                          </Badge>
                          {c.decidedAt && (
                            <div className="mt-1 text-ink-3">
                              {t('admin.ads.decidedBy', {
                                name: c.deciderName ?? '—',
                                at: f.at(c.decidedAt),
                              })}
                              {c.decisionNote && <div className="text-ink-2">{c.decisionNote}</div>}
                            </div>
                          )}
                          {c.appliedAt && (
                            <div className="text-ink-3">
                              {t('admin.ads.appliedAt', { at: f.at(c.appliedAt) })}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap justify-end gap-2">
                            {c.status === 'pending' && approve && (!own || roleKey === 'owner') && (
                              <Button size="sm" variant="primary" onClick={() => setDeciding(c)}>
                                {t('admin.ads.decide')}
                              </Button>
                            )}
                            {c.status === 'approved' && manage && (
                              <ConfirmButton
                                variant="primary"
                                message={t('admin.ads.markAppliedConfirm')}
                                onConfirm={() =>
                                  step.mutateAsync({ id: c.id, action: 'applied', version: c.version })
                                }
                              >
                                {t('admin.ads.markApplied')}
                              </ConfirmButton>
                            )}
                            {c.status === 'pending' && manage && (own || approve) && (
                              <ConfirmButton
                                message={t('admin.ads.cancelConfirm')}
                                onConfirm={() =>
                                  step.mutateAsync({ id: c.id, action: 'cancel', version: c.version })
                                }
                              >
                                {t('admin.ads.cancelRequest')}
                              </ConfirmButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <h3 className="mb-2 font-medium">{t('admin.ads.runsTitle')}</h3>
            {q.data.runs.length === 0 ? (
              <p className="text-sm text-ink-2">{t('admin.ads.noRuns')}</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" aria-label={t('admin.ads.runsTitle')}>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.ads.when')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.ads.account')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.ads.source')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.ads.previewPeriod')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.ads.status')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('admin.ads.runRows')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.runs.map((x) => (
                      <tr key={x.id} className="border-t border-line align-top">
                        <td className="px-3 py-2 whitespace-nowrap">{f.at(x.createdAt)}</td>
                        <td className="px-3 py-2">{x.accountName}</td>
                        <td className="px-3 py-2">
                          {x.source === 'file'
                            ? (x.fileName ?? t('admin.ads.sources.file'))
                            : t('admin.ads.sources.api')}
                          <div className="text-ink-3">{x.userName ?? t('admin.ads.automatic')}</div>
                        </td>
                        <td className="px-3 py-2">
                          {x.periodFrom && x.periodTo ? `${f.day(x.periodFrom)} – ${f.day(x.periodTo)}` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={runTone[x.status] ?? 'neutral'}>
                            {t(`admin.ads.runStatuses.${x.status}`)}
                          </Badge>
                          {x.status === 'failed' && x.errors[0] && (
                            <div className="text-rose">{t(`integrationMessages.${x.errors[0].code}`)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {t('admin.ads.runRowsValue', {
                            inserted: x.inserted,
                            updated: x.updated,
                            skipped: x.skipped,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {deciding && (
        <DecideDialog change={deciding} describe={describe(deciding)} onClose={() => setDeciding(null)} />
      )}
    </Section>
  );
}

function DecideDialog({
  change,
  describe,
  onClose,
}: {
  change: AdChangeRequest;
  describe: string;
  onClose: () => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  const decide = useApiMutation(
    (approve: boolean) =>
      api.post(`/v1/admin/ads/changes/${change.id}/decide`, {
        approve,
        note: note.trim() || undefined,
        version: change.version,
      }),
    {
      invalidate: [['ads-history']],
      success: (_r) => t('admin.ads.changeUpdated'),
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.ads.decideTitle')}
      description={t('admin.ads.decideLead', { campaign: change.campaignName, change: describe })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={decide.isPending && decide.variables === false}
            onClick={() => decide.mutate(false)}
          >
            {t('admin.ads.reject')}
          </Button>
          <HelpTip k="ads.reject" />
          <Button
            variant="primary"
            loading={decide.isPending && decide.variables === true}
            onClick={() => decide.mutate(true)}
          >
            {t('admin.ads.approve')}
          </Button>
          <HelpTip k="ads.approve" />
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm">{change.note}</p>
        <Field
          label={t('admin.ads.decisionNote')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="ads.decision_note" />}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Google Ads dönüşüm yükleme

/**
 * Google Ads çevrimdışı dönüşüm yükleme (F6-18, ADR-099). Varsayılan kapalı: açıldığında pazarlama çerezi izni
 * veren ziyaretçilerin ödenmiş siparişleri (gclid, tutar, sipariş no) Google'a gider — kişisel verinin yurt dışına
 * aktarımı; açmak onay yetkisi ve iki adımlı doğrulama ister.
 */
function GoogleConversionsSection() {
  const fmt = useMoney();
  const { can } = usePermissions();
  const approve = can('marketing.ads.approve');
  const q = useQuery({
    queryKey: ['google-ads-settings'],
    queryFn: () => api.get<GoogleAdsSettings>('/v1/admin/ads/google-ads/settings'),
  });
  if (!q.data) return null;
  return <GoogleConversionsForm key={JSON.stringify(q.data)} saved={q.data} approve={approve} fmt={fmt} />;
}

function GoogleConversionsForm({
  saved,
  approve,
  fmt,
}: {
  saved: GoogleAdsSettings;
  approve: boolean;
  fmt: ReturnType<typeof useMoney>;
}) {
  const t = useT();
  const [enabled, setEnabled] = useState(saved.conversionsEnabled);
  const [action, setAction] = useState(saved.conversionAction ?? '');
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/ads/google-ads/settings', {
        conversionsEnabled: enabled,
        conversionAction: action.trim() || null,
      }),
    { invalidate: [['google-ads-settings']], success: t('admin.ads.gcSaved'), silentFieldErrors: true },
  );
  const sum = (status: string) => saved.uploads.filter((u) => u.status === status);
  return (
    <Section
      title={t('admin.ads.gcTitle')}
      description={t('admin.ads.gcLead')}
      action={<HelpTip k="ads.google_conversions" />}
    >
      <div className="space-y-4">
        <div role="note" className="rounded-md border border-amber/40 bg-amber/10 p-3 text-sm">
          {t('admin.ads.gcPrivacy')}
        </div>
        <div className="flex items-start gap-3">
          <Switch id="gc-enabled" checked={enabled} disabled={!approve} onCheckedChange={setEnabled} />
          <div>
            <label htmlFor="gc-enabled" className="font-medium">
              {t('admin.ads.gcEnabled')}
            </label>
            <p id="gc-enabled-hint" className="text-sm text-ink-2">
              {t('admin.ads.gcEnabledHint')}
            </p>
          </div>
          <HelpTip k="ads.google_conversions_enabled" />
        </div>
        <Field
          label={t('admin.ads.gcAction')}
          description={t('admin.ads.gcActionHint')}
          help={<HelpTip k="ads.google_conversion_action" />}
          error={fieldError(save.error, 'conversionAction')}
        >
          {(p) => (
            <Input
              {...p}
              value={action}
              disabled={!approve}
              placeholder="customers/1234567890/conversionActions/123456789"
              onChange={(e) => setAction(e.target.value)}
            />
          )}
        </Field>
        {approve && (
          <div className="flex items-center gap-1">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('common.save')}
            </Button>
            <HelpTip k="ads.google_conversions_save" />
          </div>
        )}
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
        <p className="text-sm text-ink-2">
          {saved.uploads.length === 0
            ? t('admin.ads.gcNoUploads')
            : [
                ...sum('uploaded').map((u) =>
                  t('admin.ads.gcUploaded', { count: u.count, value: fmt(u.valueMinor, u.currency) }),
                ),
                ...sum('failed').map((u) => t('admin.ads.gcFailed', { count: u.count })),
              ].join(' · ')}
        </p>
      </div>
    </Section>
  );
}
